<template>
  <div>
    <v-form
        ref="form"
        v-model="valid"
        class="explorer-configuration"
        @input="$emit('valid-change', $event);"
        :disabled="disabled">
      <XSwitch
          :disabled="disabled"
          v-model="dataValue.active"
          label="Explorer active"
          type="numberString"
          @input="emitInput"
          help="explorer_config_switch_active"/>
      <div class="top-part">
        <div class="top-left-part">
          <InputRow>
            <XTextField
                help="explorer_config_alias"
                :disabled="disabled"
                v-model="dataValue.alias"
                label="Alias"
                class="explorer-configuration-input-row-child"
                @input="emitInput"/>
            <XSelect
                help="explorer_config_device_pool"
                :disabled="disabled"
                :value="dataValue.devicePool"
                label="Device Pool"
                :items="devicePools"
                :item-text="formatDevicePool"
                required
                class="explorer-configuration-input-row-child"
                @input="checkDevicePoolChange"/>
          </InputRow>
          <ExplorerTagSelect
              help="explorer_config_tags"
              :disabled="disabled"
              v-model="dataValue.tags"
              @input="emitInput"/>
          <InputRow expand-children>
            <XTextField
                help="explorer_config_latitude"
                :disabled="disabled"
                type="float"
                v-model="dataValue.configuration.location.latitude"
                label="Latitude"
                class="explorer-configuration-input-row-child"
                @input="emitInput"/>
            <XTextField
                help="explorer_config_longitude"
                :disabled="disabled"
                type="float"
                v-model="dataValue.configuration.location.longitude"
                label="Longitude"
                class="explorer-configuration-input-row-child"
                @input="emitInput"/>
          </InputRow>
        </div>
        <XTextarea
            help="explorer_config_description"
            v-model="dataValue.description"
            :class="`description ${dataValue.description ? '' : 'empty'}`"
            label="Description"
            @input="emitInput"/>
      </div>
      <HeadlineBox
          headline="IP Configuration"
          padded
          v-if="value?.type!=='sim-server'"
          help="explorer_config_ip_config_header">
        <div class="ip-configuration">
          <object-input-row
              v-model="dataValue.configuration.testing"
              :columns="TESTING_COLS"
              @input="emitInput"/>
          <object-input-row
              v-model="dataValue.configuration.network"
              :columns="ipTypeColumns"
              @input="emitInput"/>
          <div v-if="dataValue.configuration.network.type === 'static'" class="ip-types">
            <object-input-row
                v-model="dataValue.configuration.network.ipv4"
                :columns="IPV4_COLS"
                @input="emitInput"/>
            <object-input-row
                v-model="dataValue.configuration.network.ipv6"
                :columns="IPV6_COLS"
                @input="emitInput"/>
          </div>
        </div>
      </HeadlineBox>
    </v-form>
    <YesNoDialog
        v-model="selectBusinessStatusDialog"
        title="Select Business Status"
        @yes="setNewDevicePool"
        yes-text="Select"
        yes-color="primary"
        no-text="Cancel">
      <div class="select-business-status-dialog-form">
        <div>
          The new device pool has a different business workflow.
        </div>
        <div>
          Please select a business status from the new business workflow to assign to the explorer.
        </div>
        <XSelect v-model="newBusinessStatus" :items="businessStatuses" item-value="value" item-text="text" required/>
      </div>
    </YesNoDialog>
    <LoadingDialog v-model="loading"/>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import XTextField from '@/components/basic/XTextField.vue';
import XTextarea from '@/components/basic/XTextarea.vue';
import ObjectInputRow from '@/components/basic/ObjectInputRow.vue';
import XSwitch from '@/components/basic/XSwitch.vue';
import HeadlineBox from '@/components/basic/HeadlineBox.vue';
import ExplorerTagSelect from "@/components/specific/Explorer/ExplorerTagSelect.vue";
import {deepCopy, getRequiredMessage, setDefaultValues} from '@/js/general';
import XSelect from '@/components/basic/XSelect.vue';
import cockpitExplorerServiceOld from '@/js/services/CockpitExplorerService';
import { cockpitExplorerService } from "@/api";
import InputRow from '@/components/basic/InputRow.vue';
import LoadingDialog from '@/components/basic/LoadingDialog.vue';
import YesNoDialog from '@/components/extended/YesNoDialog.vue';

const TESTING_COLS = [
  {
    type: 'checkbox',
    value: 'enableIPv4',
    label: 'Enable IPv4 Testing',
    help: 'explorer_config_ip_config_enableIPv4'
  },
  {
    type: 'checkbox',
    value: 'enableIPv6',
    label: 'Enable IPv6 Testing',
    help: 'explorer_config_ip_config_enableIPv6'
  },
];

const IPV4_COLS = [
  {
    type: 'span',
    value: 'v4',
  },
  {
    type: 'text',
    value: 'ip',
    label: 'IP address',
    width: '20%',
    required: true,
    rules: ['ipv4'],
    help: 'explorer_config_ip_config_ipv4_address',
  },
  {
    type: 'text',
    value: 'maskOrCidr',
    label: 'Mask/CIDR',
    width: '20%',
    rules: ['maskOrCidr'],
    help: 'explorer_config_ip_config_ipv4_maskOrCidr',
  },
  {
    type: 'text',
    value: 'gateway',
    label: 'Gateway',
    required: true,
    rules: ['ipv4'],
    help: 'explorer_config_ip_config_ipv4_gateway',
  },
  {
    type: 'text',
    value: 'dns1',
    label: 'DNS1',
    rules: ['ipv4'],
    help: 'explorer_config_ip_config_ipv4_dns1',
  },
  {
    type: 'text',
    value: 'dns2',
    label: 'DNS2',
    rules: ['ipv4'],
    help: 'explorer_config_ip_config_ipv4_dns2',
  },
];

const IPV6_COLS = [
  {
    type: 'span',
    value: 'v6',
  },
  {
    type: 'text',
    value: 'ip',
    label: 'IP address',
    width: 'calc(40% + 10px)',
    rules: ['ipv6'],
    help: 'explorer_config_ip_config_ipv6_address',
  },
  {
    type: 'text',
    value: 'gateway',
    label: 'Gateway',
    rules: [v => !this.dataValue.configuration.network.ipv6.ip || !!v || getRequiredMessage(), 'ipv6'],
    help: 'explorer_config_ip_config_ipv6_gateway',
  },
  {
    type: 'text',
    value: 'dns1',
    label: 'DNS1',
    rules: ['ipv6'],
    help: 'explorer_config_ip_config_ipv6_dns1',
  },
  {
    type: 'text',
    value: 'dns2',
    label: 'DNS2',
    rules: ['ipv6'],
    help: 'explorer_config_ip_config_ipv6_dns2',
  },
];

export default defineComponent({
  name: 'ExplorerConfiguration',

  components: {
    YesNoDialog,
    LoadingDialog,
    InputRow,
    XSelect,
    ExplorerTagSelect,
    HeadlineBox,
    ObjectInputRow,
    XSwitch,
    XTextField,
    XTextarea,
  },

  props: {
    value: Object,
    disabled: Boolean,
  },

  setup() {
    return {
      TESTING_COLS,
      IPV4_COLS,
      IPV6_COLS,
    };
  },
  data() {
    return {
      dataValue: null,
      ipTypeColumns: Object.freeze([
        {
          type: 'radio',
          value: 'type',
          direction: 'row',
          items: [
            {
              value: 'dhcp',
              text: 'DHCP',
              help: 'explorer_config_ip_config_dhcp'
            },
            {
              value: 'static',
              text: 'Static',
              help: 'explorer_config_ip_config_static'
            },
          ],
        },
        {
          type: 'spacer',
        },
        {
          type: 'span',
          value: 'Fallback to DHCP after',
          condition: () => this.dataValue.configuration.network.type === 'static',
        },
        {
          help: 'explorer_config_ip_config_fallback',
          type: 'int',
          value: 'fallback',
          width: 60,
          required: true,
          condition: () => this.dataValue.configuration.network.type === 'static',
        },
        {
          type: 'span',
          value: 's.',
          condition: () => this.dataValue.configuration.network.type === 'static',
        },
      ]),
      valid: false,
      devicePools: [],
      loading: false,
      businessStatuses: [],
      selectBusinessStatusDialog: false,
      newDevicePool: null,
      newBusinessStatus: '',
      showNewBusinessStatus: false,
    };
  },

  async created() {
    this.devicePools = await cockpitExplorerService.v1.getDevicePools();
  },

  mounted() {
    this.$nextTick(() => {
      this.$nextTick(() => {
        this.$refs.form.validate();
        this.$nextTick(() => {
          this.$emit('valid-change', this.valid);
        });
      });
    });
  },
  watch: {
    value: {
      immediate: true,
      handler(value) {
        if (value !== this.dataValue) {
          setDefaultValues(value, this.createDataValue());
          this.dataValue = value;
        }
      },
    },
  },
  methods: {
    createDataValue() {
      return {
        alias: '',
        tags: [],
        description: '',
        active: true,
        configuration: {
          network: {
            type: 'dhcp',
            fallback: 60,
            ipv4: {},
            ipv6: {},
          },
          testing: {
            enableIPv4: true,
            enableIPv6: true,
          },
          location: {
            latitude: '',
            longitude: '',
          },
        },
      };
    },
    emitInput() {
      this.$emit('input', deepCopy(this.dataValue));
      this.$refs.form.validate();
    },
    checkDevicePoolChange(devicePool) {
      this.loading = true;
      this.newDevicePool = devicePool;
      cockpitExplorerServiceOld.getBusinessStatusesByDevicePool(
          devicePool.id,
          this.dataValue.devicePool.id,
          (businessStatuses) => {
            if (businessStatuses.length) {
              this.businessStatuses = businessStatuses;
              this.selectBusinessStatusDialog = true;
            } else {
              this.setNewDevicePool(true);
            }
            this.loading = false;
          },
      );
    },
    setNewDevicePool(noBusinessStatus) {
      this.dataValue.devicePool = this.newDevicePool;
      if (!noBusinessStatus) {
        this.showNewBusinessStatus = true;
        this.dataValue.status.current = this.newBusinessStatus;
      }
      this.emitInput();
    },
    formatDevicePool(devicePool) {
      let formattedDevicePool = devicePool.name;
      if (this.showNewBusinessStatus) {
        formattedDevicePool += ` (${this.businessStatuses.find(x => x.value === this.newBusinessStatus).text})`;
      }
      return formattedDevicePool;
    },
  },
});
</script>

<style scoped>
.explorer-configuration {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.top-part {
  display: flex;
  gap: 16px;
}

.top-part > * {
  flex: 1 1 50%;
}

.top-left-part {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.explorer-configuration-input-row-child {
  flex-basis: 50%;
}

.location > * {
  flex: 1 1 auto;
}

.description >>> .v-input__control {
  height: calc(100% + 4px);
}

.description >>> .v-input__slot {
  height: calc(100% + 4px);
}

.ip-configuration, .ip-types {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.select-business-status-dialog-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
</style>
